import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllVoucherClaimedReportList } from 'app/api/report';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const useVoucherClaimedDataReport = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [values, setValues] = useState({
    mallName: '',
    phone: '',
    membershipType: '',
    voucherColledtedDateStart: '',
    voucherColledtedDateEnd: '',
  });
  const [totalNumber, setTotalNumber] = useState(0);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const dataPerPage = 10;
  const [isFiltering, setIsFiltering] = useState(false); // New state to track filtering
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sectionObj = {
    sectionTitle: 'Voucher Claimed Data Report',
    searchObj: {
      keys: [''],
      placeholder: 'Search  Name , Mall , Contact No.',
      searchType: 'server',
      handleSearch: () => {},
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen(!isFilterOpen),
      },
    ],
    downloadObj: {
      excelClick: (e) => {
        handleDownloadexcel(e);
      },
      csvClick: (e) => {
        handleDownloadcsv(e);
      },

      pdfClick: () => () => {},
      inProgress: downloadInProgress,
    },
  };
  const fetchReportData = useCallback(
    async (page, search = '', filters = {}) => {
      setPageLoading(true);
      try {
        let requestParam = {
          page,
          limit: dataPerPage,
          search,
          mallName: filters.mallName,
          phone: filters.phone,
          membershipType: filters.membershipType,
          voucherColledtedDateStart: filters.voucherColledtedDateStart,
          voucherColledtedDateEnd: filters.voucherColledtedDateEnd,
        };
        const response = await getAllVoucherClaimedReportList(requestParam);

        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          const newArray = dataArr.map((item) => ({
            ...item,
            issuedByDetail:`${item?.managerDetails?.name} (${item?.managerDetails?.phoneNumber})`         
          }));

          setData(newArray);
          setFilteredData(newArray);

          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );
  useEffect(() => {
    if (isFiltering) {
      handleFiltering();
      fetchReportData(1, '', values);
      setIsFiltering(false);
    }
  }, [isFiltering, data, values, filteredData]);
  function handleFiltering() {
    const filtered = data.filter((item) => {
      let ismallNameMatching =
        !values.mallName ||
        item?.mallName?.toLowerCase().includes(values.mallName.toLowerCase());

      let isPhoneMatching =
        !values.phone ||
        item?.phone?.toLowerCase().includes(values.phone.toLowerCase());

      let isMembershipTypeMatching =
        !values.membershipType ||
        item?.membershipType
          ?.toLowerCase()
          .includes(values.membershipType.toLowerCase());

      let isVoucherCollectedDateStartMatching =
        !values.voucherColledtedDateStart ||
        new Date(item?.collectDate) >=
          new Date(values.voucherColledtedDateStart);

      let isVoucherCollectedDateEndMatching =
        !values.voucherColledtedDateEnd ||
        new Date(item?.collectDate) <= new Date(values.voucherColledtedDateEnd);

      return (
        ismallNameMatching &&
        isPhoneMatching &&
        isMembershipTypeMatching &&
        isVoucherCollectedDateStartMatching &&
        isVoucherCollectedDateEndMatching
      );
    });

    setFilteredData(filtered);
  }
  function handleSubmit(event) {
    event.preventDefault();
    setIsFiltering(true);
  }
  function resetfilterModel() {
    setValues({
      mallName: '',
      phone: '',
      membershipType: '',
      voucherColledtedDateStart: '',
      voucherColledtedDateEnd: '',
    });
    setFilteredData([]);
    setIsFiltering(true);
  }
  function handleFilterCancel() {
    setIsFilterOpen(false);
  }
  // Async export: returns a jobId, then polls /export/status/:jobId. We DON'T
  // block the UI with pageLoading — the Toast is enough feedback, and forcing
  // a full-page loader prevents the user from working while the export runs
  // (which can take minutes for large datasets).
  async function startDownload(downloadType) {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    let filters = values;
    let requestParam = {
      page: 1,
      limit: totalNumber,
      search: '',
      mallName: filters.mallName,
      phone: filters.phone,
      membershipType: filters.membershipType,
      voucherColledtedDateStart: filters.voucherColledtedDateStart,
      voucherColledtedDateEnd: filters.voucherColledtedDateEnd,
      download: true,
      downloadType,
    };

    try {
      const response = await getAllVoucherClaimedReportList(requestParam);
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
        return;
      }

      Toast.success('Report generation started');

      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
      });
    } catch (error) {
      console.error('startDownload failed:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    }
  }

  async function handleDownloadcsv() {
    return startDownload('csv');
  }

  async function handleDownloadexcel() {
    return startDownload('excel');
  }
  return {
    pageLoading,
    filteredData,
    fetchReportData,
    navigate,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    resetfilterModel,
    handleSubmit,
    handleFilterCancel,
    isFiltering,
  };
};

export default useVoucherClaimedDataReport;
